import axios from 'axios'
import { getAccessToken, setAccessToken, setPrimaryColor, clearAccessToken } from './auth'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000'

export const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
})

api.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Fila de requests que chegaram enquanto um refresh já estava em voo
type QueueEntry = { resolve: (token: string) => void; reject: (err: unknown) => void }
let isRefreshing = false
let pendingQueue: QueueEntry[] = []

function drainQueue(error: unknown, token: string | null) {
  for (const { resolve, reject } of pendingQueue) {
    if (error || token === null) reject(error)
    else resolve(token)
  }
  pendingQueue = []
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status as number | undefined
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const original = error?.config

    if (status === 401 && original && !original._retry) {
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          pendingQueue.push({ resolve, reject })
        }).then((token) => {
          original.headers.Authorization = `Bearer ${token}`
          return api(original)
        })
      }

      original._retry = true
      isRefreshing = true

      try {
        const { data } = await axios.post<{ accessToken: string; primaryColor: string }>(
          `${BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true },
        )
        setAccessToken(data.accessToken)
        setPrimaryColor(data.primaryColor)
        drainQueue(null, data.accessToken)
        original.headers.Authorization = `Bearer ${data.accessToken}`
        return api(original)
      } catch (refreshError) {
        drainQueue(refreshError, null)
        clearAccessToken()
        globalThis.location.href = '/login'
        throw refreshError
      } finally {
        isRefreshing = false
      }
    }

    if (status === 403) {
      globalThis.location.href = '/403'
    }

    throw error
  },
)
